import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaQuoteLeft, FaFileInvoice, FaPrint } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Quotations.css';
import { formatShortDate } from '../../utils/date';

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);

  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [lastCreatedQuotation, setLastCreatedQuotation] = useState(null);
  const itemInputRef = useRef(null);

  const [formData, setFormData] = useState({
    company: '',
    customer: '',
    customerName: '', // For display/manual entry
    items: [],
    validUntil: '',
    notes: ''
  });

  const [newItem, setNewItem] = useState({
    inventory: '',
    quantity: 1,
    sellingPrice: 0,
    discount: 0,
    itemCode: '',
    mrp: 0
  });

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' }
  ];

  useEffect(() => {
    loadQuotations();
    loadCompanies();
    loadCustomers();
    loadInventoryItems();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, companyFilter, statusFilter]);

  const loadQuotations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (companyFilter) params.append('company', companyFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const response = await api.get(`/api/quotations?${params.toString()}`);
      const rawQuotations = response.data.quotations || [];

      // Sort: Today's records first, then by date descending
      const today = new Date().setHours(0, 0, 0, 0);
      const sorted = [...rawQuotations].sort((a, b) => {
        const dateA = new Date(a.createdAt).setHours(0, 0, 0, 0);
        const dateB = new Date(b.createdAt).setHours(0, 0, 0, 0);

        if (dateA === today && dateB !== today) return -1;
        if (dateA !== today && dateB === today) return 1;

        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setQuotations(sorted);
    } catch (err) {
      console.error('Error loading quotations:', err);
      setError('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/api/customers?limit=1000');
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadInventoryItems = async () => {
    try {
      const response = await api.get('/api/inventory');
      setInventoryItems(response.data.inventoryItems || []);
    } catch (err) {
      console.error('Error loading inventory items:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        // Use the correct API route mounted under /api/quotations
        await api.delete(`/api/quotations/${id}`);
        setQuotations(quotations.filter(q => q._id !== id));
      } catch (err) {
        console.error('Error deleting quotation:', err);
        setError('Failed to delete quotation');
      }
    }
  };

  const openCreateModal = () => {
    setFormData({
      company: '',
      items: [],
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      notes: ''
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (quotation) => {
    setEditingQuotation(quotation);
    setFormData({
      company: quotation.company?._id || '',
      items: quotation.items || [],
      validUntil: quotation.validUntil ? new Date(quotation.validUntil).toISOString().split('T')[0] : '',
      notes: quotation.notes || ''
    });
    setShowModal(true);
    setError('');
  };

  const openViewModal = (quotation) => {
    setViewingQuotation(quotation);
  };

  const openLedgerForQuotation = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingQuotation(null);
    setViewingQuotation(null);
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setItemSearch('');
    setShowItemDropdown(false);
    setError('');
  };

  const handlePrint = (quotation) => {
    // Simple print implementation - in a real app you might want to render a specific print component
    // For now, we'll rely on the view modal or a dedicated print route which we don't have.
    // Assuming "directly print" means browser print behavior.

    // We can open a new window with the quotation details for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Quotation ${quotation.quotationNo}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .meta { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .totals { margin-top: 20px; text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Quotation</h1>
            <h3>${quotation.quotationNo}</h3>
          </div>
          <div class="meta">
            <p><strong>To:</strong> ${quotation.customer?.firstName ? `${quotation.customer.firstName} ${quotation.customer.lastName}` : (quotation.customerName || quotation.company?.name || 'N/A')}</p>
            <p><strong>Date:</strong> ${new Date(quotation.createdAt).toLocaleDateString()}</p>
            <p><strong>Valid Until:</strong> ${new Date(quotation.validUntil).toLocaleDateString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${quotation.items.map(item => `
                <tr>
                  <td>${item.inventory?.itemName || item.itemName || ''}</td>
                  <td>${item.quantity}</td>
                  <td>${item.sellingPrice.toFixed(2)}</td>
                  <td>${item.discount}%</td>
                  <td>${item.totalAmount.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="totals">
            <p>Subtotal: ${quotation.subtotal.toFixed(2)}</p>
            <p>Discount: ${quotation.totalDiscount.toFixed(2)}</p>
            <h3>Total: ${quotation.totalAmount.toFixed(2)}</h3>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset selected newItem details when company/customer changes
    if (name === 'company' || name === 'customer') {
      setNewItem(prev => ({
        ...prev,
        inventory: '',
        sellingPrice: 0,
        discount: 0,
        itemCode: '',
        mrp: 0
      }));
    }
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setFormData(prev => ({ ...prev, customerName: value }));

    if (value.length >= 2) {
      const filtered = customers.filter(c =>
        c.firstName.toLowerCase().includes(value.toLowerCase()) ||
        c.lastName.toLowerCase().includes(value.toLowerCase()) ||
        c.email.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCustomers(filtered);
      setShowCustomerDropdown(true);
      setActiveCustomerIndex(-1);
    } else {
      setShowCustomerDropdown(false);
    }
  };

  const handleCustomerKeyDown = (e) => {
    if (!showCustomerDropdown || filteredCustomers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev < filteredCustomers.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeCustomerIndex >= 0 && activeCustomerIndex < filteredCustomers.length) {
        selectCustomer(filteredCustomers[activeCustomerIndex]);
      }
    }
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.isQuotationOnly ? '' : customer._id,
      customerName: customer.isQuotationOnly ? customer.firstName : `${customer.firstName} ${customer.lastName}`,
      company: '' // Clear company if customer is selected
    }));
    setCustomerSearch(`${customer.firstName} ${customer.lastName}`);
    setShowCustomerDropdown(false);
  };

  const handleItemSearch = (e) => {
    const value = e.target.value;
    setItemSearch(value);

    if (value.length >= 2) {
      const filtered = inventoryItems.filter(item =>
        (item.itemName || '').toLowerCase().includes(value.toLowerCase()) ||
        (item.itemCode || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredItems(filtered);
      setShowItemDropdown(true);
      setActiveItemIndex(-1);
    } else {
      setShowItemDropdown(false);
    }
  };

  const handleItemKeyDown = (e) => {
    if (!showItemDropdown || filteredItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveItemIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveItemIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeItemIndex >= 0 && activeItemIndex < filteredItems.length) {
        selectItem(filteredItems[activeItemIndex]);
      }
    }
  };

  const selectItem = (item) => {
    setNewItem(prev => ({
      ...prev,
      inventory: item._id,
      sellingPrice: item.sellingPrice || 0,
      itemCode: item.itemCode || '',
      mrp: item.mrp || 0
    }));
    setItemSearch(`${item.itemCode} - ${item.itemName}`);
    setShowItemDropdown(false);
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    // Inventory selection is handled by selectItem now, but we keep this for legacy or direct updates if needed
    setNewItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addItem = () => {
    if (!newItem.inventory || !newItem.quantity || !newItem.sellingPrice) {
      alert('Please fill in all item fields');
      return;
    }

    const selectedInventory = inventoryItems.find(item => item._id === newItem.inventory);
    if (!selectedInventory) return;

    const itemTotal = (newItem.sellingPrice - (newItem.sellingPrice * newItem.discount / 100)) * newItem.quantity;

    const item = {
      inventory: newItem.inventory,
      quantity: parseInt(newItem.quantity),
      sellingPrice: parseFloat(newItem.sellingPrice),
      discount: parseFloat(newItem.discount),
      totalAmount: itemTotal,
      itemName: selectedInventory.itemName,
      itemCode: selectedInventory.itemCode
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    setNewItem({
      inventory: '',
      quantity: 1,
      sellingPrice: 0,
      discount: 0,
      itemCode: '',
      mrp: 0
    });
    setItemSearch(''); // Clear item search

    if (itemInputRef.current) {
      itemInputRef.current.focus();
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
    const totalDiscount = formData.items.reduce((sum, item) => sum + (item.sellingPrice * item.discount / 100 * item.quantity), 0);
    const totalAmount = subtotal - totalDiscount;
    return { subtotal, totalDiscount, totalAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if ((!formData.company && !formData.customer && !formData.customerName) || formData.items.length === 0) {
        throw new Error('Please select a company or customer and add at least one item');
      }

      const { subtotal, totalDiscount, totalAmount } = calculateTotals();

      const quotationData = {
        ...formData,
        subtotal,
        totalDiscount,
        totalAmount,
        validUntil: formData.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };

      if (editingQuotation) {
        const response = await api.put(`/api/quotations/${editingQuotation._id}`, quotationData);
        setQuotations(quotations.map(q => q._id === editingQuotation._id ? response.data.quotation : q));
        closeModal();
      } else {
        const response = await api.post('/api/quotations', quotationData);
        setQuotations([response.data.quotation, ...quotations]);
        setLastCreatedQuotation(response.data.quotation);
        closeModal();
        setShowPrintConfirm(true);
      }

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save quotation');
    } finally {
      setLoading(false);
    }
  };

  const createBillingFromQuotation = async (quotationId) => {
    try {
      setLoading(true);
      const response = await api.post('/api/billing/from-quotation', {
        quotationId,
        paymentMethod: 'cash',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      });
      alert('Billing created successfully!');
      // Optionally reload quotations to update status
      loadQuotations();
    } catch (err) {
      console.error('Error creating billing from quotation:', err);
      setError('Failed to create billing from quotation');
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotations = quotations.filter(q =>
    q.quotationNo.toLowerCase().includes(search.toLowerCase()) ||
    (q.company?.name && q.company.name.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredQuotations.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedQuotations = filteredQuotations.slice(pageStart, pageEnd);

  if (loading && quotations.length === 0) return <Loading />;

  return (
    <div className="quotations-container">
      <div className="quotations-header">
        <div>
          <h1 className="quotations-title">Quotation Management</h1>
          <p className="quotations-subtitle">Create, edit, and manage quotations</p>
        </div>
        <button className="add-quotation-btn" onClick={openCreateModal}>
          <FaPlus /> Create Quotation
        </button>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search quotations by number or company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="filter-row">
          <select
            value={companyFilter}
            onChange={e => setCompanyFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <button onClick={loadQuotations} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="quotations-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredQuotations.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredQuotations.length, pageEnd)} of {filteredQuotations.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
        <table className="quotations-table">
          <thead>
            <tr>
              <th>Quotation No</th>
              <th>Customer/Company</th>
              {/* Removed Item Field */}
              <th>Total Amount</th>
              <th>Status</th>
              <th>Valid Until</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotations.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>No quotations found.</td>
              </tr>
            ) : (
              paginatedQuotations.map(quotation => (
                <tr key={quotation._id}>
                  <td>
                    <div className="quotation-number-cell">
                      <FaQuoteLeft className="quotation-icon" />
                      <span>{quotation.quotationNo}</span>
                    </div>
                  </td>
                  <td>{quotation.customer?.firstName ? `${quotation.customer.firstName} ${quotation.customer.lastName}` : (quotation.customerName || quotation.company?.name || 'N/A')}</td>
                  {/* Removed Items Cell */}
                  <td>₹{quotation.totalAmount?.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${quotation.status}`}>
                      {quotation.status}
                    </span>
                  </td>
                  <td>
                    {formatShortDate(quotation.validUntil)}
                  </td>
                  <td>
                    {formatShortDate(quotation.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => handlePrint(quotation)}
                        className="action-btn print"
                        title="Print Quotation"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        onClick={() => openViewModal(quotation)}
                        className="action-btn view"
                        title="View Quotation"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => openEditModal(quotation)}
                        className="action-btn edit"
                        title="Edit Quotation"
                      >
                        <FaEdit /> Edit
                      </button>
                      {quotation.status === 'accepted' && (
                        <button
                          onClick={() => createBillingFromQuotation(quotation._id)}
                          className="action-btn billing"
                          title="Create Billing"
                        >
                          <FaFileInvoice /> Billing
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(quotation._id)}
                        className="action-btn delete"
                        title="Delete Quotation"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingQuotation ? 'Edit Quotation' : 'Create New Quotation'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="quotation-form">
              <div className="form-section">
                <h3>Quotation Information</h3>
                <div className="form-row">
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label htmlFor="customerName">Customer Name *</label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleCustomerSearch}
                      onKeyDown={handleCustomerKeyDown}
                      placeholder="Type at least 2 chars to search..."
                      autoComplete="off"
                      required={!formData.company}
                    />
                    {showCustomerDropdown && (
                      <div className="dropdown-list" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}>
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map((customer, index) => (
                            <div
                              key={customer._id}
                              onClick={() => selectCustomer(customer)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                backgroundColor: activeCustomerIndex === index ? '#f0f0f0' : 'transparent'
                              }}
                              className={`dropdown-item ${activeCustomerIndex === index ? 'active' : ''}`}
                            >
                              {customer.firstName} {customer.lastName && !customer.isQuotationOnly ? customer.lastName : ''} {customer.isQuotationOnly ? '(from Quotation)' : ''} {customer.email ? `(${customer.email})` : ''}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '8px 12px', color: '#666' }}>No customers found</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="validUntil">Valid Until</label>
                    <input
                      type="date"
                      id="validUntil"
                      name="validUntil"
                      value={formData.validUntil}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter quotation notes"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Add Items</h3>
                <div className="add-item-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="inventory">Select Item</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          id="inventory"
                          name="inventory"
                          value={itemSearch}
                          onChange={handleItemSearch}
                          onKeyDown={handleItemKeyDown}
                          ref={itemInputRef}
                          placeholder="Type at least 2 chars to search..."
                          autoComplete="off"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
                        />
                        {showItemDropdown && (
                          <div className="dropdown-list" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                          }}>
                            {filteredItems.length > 0 ? (
                              filteredItems.map((item, index) => (
                                <div
                                  key={item._id}
                                  onClick={() => selectItem(item)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: activeItemIndex === index ? '#f0f0f0' : 'transparent'
                                  }}
                                  className={`dropdown-item ${activeItemIndex === index ? 'active' : ''}`}
                                >
                                  {item.itemCode} - {item.itemName} (₹{item.sellingPrice})
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '8px 12px', color: '#666' }}>No items found</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="quantity">Quantity</label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        value={newItem.quantity}
                        onChange={handleNewItemChange}
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="itemCode">Item Code</label>
                      <input
                        type="text"
                        id="itemCode"
                        name="itemCode"
                        value={newItem.itemCode}
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="mrp">MRP</label>
                      <input
                        type="number"
                        id="mrp"
                        name="mrp"
                        value={newItem.mrp}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="sellingPrice">Selling Price</label>
                      <input
                        type="number"
                        id="sellingPrice"
                        name="sellingPrice"
                        value={newItem.sellingPrice}
                        onChange={handleNewItemChange}
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discount">Discount (%)</label>
                      <input
                        type="number"
                        id="discount"
                        name="discount"
                        value={newItem.discount}
                        onChange={handleNewItemChange}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <button type="button" onClick={addItem} className="add-item-btn">
                    Add Item
                  </button>
                </div>

                {formData.items.length > 0 && (
                  <div className="items-list">
                    <h4>Quotation Items</h4>
                    <div className="items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Discount</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div>
                                  <div className="item-name">{item.itemName}</div>
                                  <div className="item-code">{item.itemCode}</div>
                                </div>
                              </td>
                              <td>{item.quantity}</td>
                              <td>₹{item.sellingPrice.toFixed(2)}</td>
                              <td>{item.discount}%</td>
                              <td>₹{item.totalAmount.toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="remove-item-btn"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="totals-section">
                      <div className="total-row">
                        <span>Subtotal:</span>
                        <span>₹{calculateTotals().subtotal.toFixed(2)}</span>
                      </div>
                      <div className="total-row">
                        <span>Total Discount:</span>
                        <span>₹{calculateTotals().totalDiscount.toFixed(2)}</span>
                      </div>
                      <div className="total-row total-amount">
                        <span>Total Amount:</span>
                        <span>₹{calculateTotals().totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingQuotation ? 'Update Quotation' : 'Create Quotation')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingQuotation && (
        <div className="modal-overlay" onClick={() => setViewingQuotation(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Quotation Details - {viewingQuotation.quotationNo}</h2>
              <button className="modal-close" onClick={() => setViewingQuotation(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="quotation-view-content">
              <div className="quotation-info-section">
                <div className="detail-group">
                  <h3>Quotation Information</h3>
                  <div className="detail-item">
                    <label>Quotation Number:</label>
                    <span>{viewingQuotation.quotationNo}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{viewingQuotation.company?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingQuotation.status}`}>
                      {viewingQuotation.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Valid Until:</label>
                    <span>{formatShortDate(viewingQuotation.validUntil)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created Date:</label>
                    <span>{formatShortDate(viewingQuotation.createdAt)}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Financial Summary</h3>
                  <div className="detail-item">
                    <label>Subtotal:</label>
                    <span>₹{viewingQuotation.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Total Discount:</label>
                    <span>₹{viewingQuotation.totalDiscount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item total-amount">
                    <label>Total Amount:</label>
                    <span>₹{viewingQuotation.totalAmount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="items-section">
                <h3>Quotation Items</h3>
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Discount</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingQuotation.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.inventory?.itemCode || item.itemCode}</td>
                          <td>{item.inventory?.itemName || item.itemName}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.sellingPrice.toFixed(2)}</td>
                          <td>{item.discount}%</td>
                          <td>₹{item.totalAmount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {viewingQuotation.notes && (
                <div className="notes-section">
                  <h3>Notes</h3>
                  <p>{viewingQuotation.notes}</p>
                </div>
              )}

              {viewingQuotation.status === 'accepted' && (
                <div className="action-section">
                  <button
                    onClick={() => createBillingFromQuotation(viewingQuotation._id)}
                    className="create-billing-btn"
                  >
                    <FaFileInvoice /> Create Billing from this Quotation
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
                <button className="btn-primary" onClick={openLedgerForQuotation}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingQuotation ? `Quotation ${viewingQuotation.quotationNo} adjustment` : ''}
      />

      {showPrintConfirm && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h2>Print Confirmation</h2>
            </div>
            <div className="modal-body">
              <p>Quotation created successfully! Would you like to print it now?</p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="action-btn print"
                onClick={() => {
                  handlePrint(lastCreatedQuotation);
                  setShowPrintConfirm(false);
                }}
              >
                <FaPrint /> Print
              </button>
              <button
                className="action-btn delete"
                onClick={() => setShowPrintConfirm(false)}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quotations;

